/**
 * The move-in address review behind one opened run (S1): its read and its one
 * answer.
 *
 * ITS OWN FILE, NOT A SIXTH HOOK IN `useAutomationsData` (Regel 48). That file
 * is the Automation Center's read budget and already near the size where a
 * file starts collecting responsibilities. This hook adds to the budget in
 * exactly one place, and says so: it asks only while a run is OPEN whose
 * stored outcome is a review skip.
 *
 * WHEN IT ASKS. The opened run's own detail decides — the run the server sent,
 * about the run and the automation that were asked for, `skipped` with
 * `manual_review_required`, `manual_address_present` or
 * `kept_address_protected`. Nothing else costs a
 * review request: a list of fifty rows costs none, any other run costs none,
 * and the admin's own answer run (a `manual` run) carries its stored answer in
 * its detail (`review_resolution`), so it costs none either. Opening a review
 * run costs one request after its detail; opening it again costs nothing
 * (page-lifetime cache, `useCachedRead`).
 *
 * WHAT IT HOLDS IS NOT THE RUN. `case` is the tenant's CURRENT open case,
 * decided live by the server when it was read, and the consumer has to say so
 * ("Aktueller Prüffall"). `resolutions` are stored answers. Neither changes
 * the run, which stays the record of what the automation did that hour.
 *
 * THE ANSWER. `resolve(decision, fingerprint)` sends exactly that pair; the
 * server decides tenant, contracts and address again inside the tenant lock.
 * One answer at a time on the page. Afterwards — success or a 409 — this
 * automation's reviews are forgotten and the open one is read again, so the
 * reader sees what is stored now rather than what was on screen; a success is
 * also reported to `onResolved`, with the automation it was given for, so the
 * page can re-read the run history the answer has just added a row to. A
 * failure is kept with the run it belongs to and shown only there, until the
 * next answer, a retry or "Aktualisieren".
 */
import { useCallback, useRef, useState } from "react";

import {
  fetchAdminAutomationRunReview,
  resolveAdminAutomationRunReview,
} from "../../../api/adminData/automations";
import useCachedRead from "./useCachedRead";

/** The skip reasons that leave a question for an admin. */
export const MOVE_IN_REVIEW_REASONS = [
  "manual_review_required",
  "manual_address_present",
  "kept_address_protected",
];

const REVIEW_ERROR = "Der Prüffall konnte nicht geladen werden.";
const RESOLVE_ERROR = "Die Entscheidung konnte nicht gespeichert werden.";

/** A run whose stored outcome is a review skip — the only runs that ask. */
export function isMoveInReviewSkip(run) {
  return (
    !!run && run.status === "skipped" && MOVE_IN_REVIEW_REASONS.includes(run.skip_reason)
  );
}

/** The cache identity of one run's review. */
function reviewId(automationKey, runId) {
  return JSON.stringify([automationKey, runId, "review"]);
}

function belongsTo(automationKey) {
  return (id) => {
    try {
      return JSON.parse(id)[0] === automationKey;
    } catch (_) {
      return false;
    }
  };
}

export default function useMoveInReview({ automationKey, runId, run, onResolved }) {
  const wanted =
    !!automationKey &&
    !!runId &&
    !!run &&
    run.id === runId &&
    run.automation_key === automationKey &&
    isMoveInReviewSkip(run);
  const id = wanted ? reviewId(automationKey, runId) : null;

  const read = useCachedRead({
    active: wanted,
    id,
    load: () => fetchAdminAutomationRunReview(automationKey, runId),
  });

  // `{ id, decision }` while an answer is on its way; one at a time.
  const [pending, setPending] = useState(null);
  const inFlight = useRef(false);
  // `{ id, message, code, status }` of the last refused answer.
  const [failure, setFailure] = useState(null);

  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  // Forgetting or re-asking also drops a refusal that is still shown: it was
  // about the case as it stood then, and the reader is now looking at it anew.
  const { refresh: refreshCache, reload: reloadRead } = read;
  const forgetAutomation = useCallback(
    (key) => {
      if (!key) return;
      setFailure(null);
      refreshCache(belongsTo(key));
    },
    [refreshCache]
  );
  const reload = useCallback(() => {
    setFailure(null);
    reloadRead();
  }, [reloadRead]);

  const resolve = useCallback(
    async (decision, fingerprint) => {
      if (!id || inFlight.current) return null;
      inFlight.current = true;
      const key = automationKey;
      const target = id;
      setPending({ id: target, decision });
      setFailure(null);
      try {
        const result = await resolveAdminAutomationRunReview(key, runId, {
          decision,
          fingerprint,
        });
        refreshCache(belongsTo(key));
        if (onResolvedRef.current) onResolvedRef.current(result, { automationKey: key });
        return result;
      } catch (error) {
        setFailure({
          id: target,
          message: error?.message || RESOLVE_ERROR,
          code: error?.code || null,
          status: error?.status || null,
        });
        // A case that changed or closed meanwhile is read again, so what the
        // reader decides next is decided on what is true now.
        if (error?.status === 409) refreshCache(belongsTo(key));
        return null;
      } finally {
        inFlight.current = false;
        setPending(null);
      }
    },
    [id, automationKey, runId, refreshCache]
  );

  return {
    wanted,
    review: read.value,
    loading: read.loading,
    error: read.error ? read.error.message || REVIEW_ERROR : "",
    reload,
    forgetAutomation,
    pending: pending && pending.id === id ? pending.decision : null,
    busy: !!pending,
    failure: failure && failure.id === id ? failure : null,
    resolve,
  };
}
